import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  authenticationContext,
  commonActions,
  commonActionUtils,
  HttpMethods,
  loggerFactory,
  uiActions
} from '@codametrix/ui-common';
import { requestJSON } from '@codametrix/client-api';
import { runSessionRenew, sendActivityHeartbeat } from '@codametrix/client-session';
import history from '../history';
import {
  displayContexts,
  fetchContexts as contextApi,
  shouldRefresh,
  tokenAvailable
} from './contexts.action';
import { jumpURL } from './jump';
import { dismissModal } from './feedback.actions';
import { loading, logout, setActiveUser } from './ui-core.actions';
import { navigateContext } from './navigation.actions';
import { dispatchSessionHeartbeatMiss } from '../core/session-heartbeat-miss';

const { UserInterface } = commonActions;
const { shimContext } = commonActionUtils;
const logger = loggerFactory('BootstrapActions');

const fetchContexts = createAsyncThunk<
  CMx.Context,
  CMx.NavData | null,
  { rejectValue: CMxCommonApp.SubmitError }
>(
  UserInterface.BOOTSTRAP,
  async (navData, { dispatch, getState }): Promise<CMx.Context> => {
    const state = getState() as any;
    // Prefer context from state if available, as navData may have been captured
    // before the authentication channel resolved and updated the state.
    const currentContext = state.ui?.context;
    const optContext = currentContext && currentContext.activeContext !== null 
      ? currentContext 
      : navData?.context;

    // in some cases the activeContext will already have been
    // determined.  if this is the case, then simply return
    // the context.  if the user needs to select the context
    // later we should provision this in the UI.
    if (optContext && optContext.activeContext !== null) {
      authenticationContext.setContextId(optContext.activeContextId);
      dispatch(
        uiActions.saveServiceLineCapability(
          optContext?.activeContext?.serviceLineName || ''
        )
      );
      dispatch(uiActions.awaitingResources(false));
      return optContext;
    } else {
      const userContexts = await contextApi(dispatch as any);

      const shimmed = shimContext(userContexts);
      const { contexts, user } = userContexts;

      dispatch(setActiveUser(user));

      if (shimmed.activeContext === null && contexts.length > 1) {
        dispatch(displayContexts(contexts));
      } else {
        const jumped = await jumpURL(shimmed, dispatch as any);
        if (!jumped) {
          dispatch(uiActions.chooseContext(shimmed));
          dispatch(navigateContext({ context: shimmed }) as any);
          dispatch(loading(false));
        }
      }
      dispatch(
        uiActions.saveServiceLineCapability(
          shimmed?.activeContext?.serviceLineName || ''
        ) as any
      );
      return shimmed;
    }
  }
);

type submitArgs = {
  name: string;
  params?: { [key: string]: any };
};

const modalSubmit = createAsyncThunk<
  void,
  submitArgs,
  { rejectValue: CMxCommonApp.SubmitError }
>(
  UserInterface.MODAL_SUBMIT,
  async (args, { dispatch }) => {
    const { name, params } = args;

    switch (name) {
      case UserInterface.LOGOUT:
        dispatch(logout());
        if (params?.isSSOUser && params?.orgCode?.length) {
          history.replace(`/sso/${params?.orgCode}`);
        } else {
          history.push(`/`);
        }
        break;
      case UserInterface.REFRESH: {
        dispatch(shouldRefresh());
        dispatch(dismissModal());
        const contextId = authenticationContext.getContextId();
        await runSessionRenew({
          role: 'initiator',
          legacyRenew: async () => {
            if (contextId == null) {
              return;
            }
            try {
              const freshContext = await requestJSON<CMxAPI.ContextAPI>(
                `/contexts/v1`,
                {
                  method: HttpMethods.PUT,
                  body: JSON.stringify({ organizationId: contextId })
                }
              );
              dispatch(
                tokenAvailable({
                  token: freshContext.jwtToken,
                  expiryTimeToLive: freshContext.expiryTimeToLive
                }) as any
              );
            } catch (e) {
              logger.error(`activity-prompt context renewal failed`, e);
            }
          },
          onToken: ({ token, expiryTimeToLive }) => {
            dispatch(tokenAvailable({ token, expiryTimeToLive }) as any);
          },
          onUnauthorized: () => dispatchSessionHeartbeatMiss(dispatch),
          onRejectNoPresence: () => {
            logger.warn('session renew rejected: no presence since last renewal');
          }
        });
        await sendActivityHeartbeat({
          contextId,
          shouldSendHeartbeat: true,
          onSessionMiss: () => dispatchSessionHeartbeatMiss(dispatch)
        });
        break;
      }
      default:
        break;
    }
  }
);

export { fetchContexts, modalSubmit };
